import React, { Component } from 'react';
import { Popover, OverlayTrigger } from 'react-bootstrap';

import { namespace } from '../../../config.json';
import PageHeader from '../components/PageHeader';
import CreatePolicyButton from '../components/CreatePolicyButton';
import LoadingSpinner from '../components/LoadingSpinner';
import EditPolicyButton from '../components/EditPolicyButton';
import DeletePolicyButton from '../components/DeletePolicyButton';

// External
import Reactable from 'reactable';

// To handle requests
import PoliciesAPI from '../utils/PoliciesAPI';

// Stylines
import brandingStyles from '../branding/brandingStyles';

// Tools
import copyToClipboard from '../utils/CopyToClipboard';
import compareDates from '../utils/compareDates';
import getDateFormat from '../utils/getDateFormat';

// Setting reactable values instead of regular table
const Table = Reactable.Table;
const Thead = Reactable.Thead;
const Th = Reactable.Th;
const Tr = Reactable.Tr;
const Td = Reactable.Td;

// PoliciesComponent Component
class PoliciesComponent extends Component {

    constructor() {
        super();

        this.state = {
            policies: [],
            filterText: '',
            currentIndex: 0,
            maxIndex: -1,
            marker: null,
            nbItems: 20,
            dataLoaded: false,
            accountId: null,
        };
        // We need to bind this to functions so we can have
        // the proper this reference inside the method
        this.refresh = this.refresh.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handlePreviousPage = this.handlePreviousPage.bind(this);
        this.handleNextPage = this.handleNextPage.bind(this);
        this.handleUserAction = this.handleUserAction.bind(this);
        this.getPolicyRow = this.getPolicyRow.bind(this);
        this.mounted = false;
    }


    componentDidMount() {
        this.mounted = true;
        this.refresh();
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    getPolicyRow(policy) {
        const actGroup =
        <div className="btn-group btn-group-xs policy-actions">
          <EditPolicyButton
            policyArn={policy.Arn}
            policyName={policy.PolicyName}
            policyVersion={policy.DefaultVersionId}
            callback={this.handleUserAction}
          />
          <DeletePolicyButton
            policyArn={policy.Arn}
            policyName={policy.PolicyName}
            callback={this.handleUserAction}
          />
        </div>;

        const arnPopover = (
            <Popover
              id="popover-user-arn-value"
              title="Amazon Resource Name (ARN)"
            >
              <p style={{ wordWrap: 'break-word' }}>
                  {policy.Arn}
              </p>
              <hr />
              <strong>
                {this.state.copiedState
                  ? 'Copied' : 'Click to copy to clipboard'}
              </strong>
            </Popover>
        );

        const arnEyeIcon = (
            <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="top"
                overlay={arnPopover}>
                    <i
                      className="fa fa-eye"
                      style={Object.assign(
                          {
                              fontSize: '18px',
                              cursor: 'pointer',
                          },
                        brandingStyles.mainColor
                      )}
                      onClick={this.copyArnToClipboard.bind(this, policy.Arn)}
                    />
            </OverlayTrigger>
        );

        return (
            <Tr
              key={policy.PolicyId}
              className="policy-row"
              style={brandingStyles.fontColor}
            >
                <Td
                  column="PolicyName"
                  data={policy.PolicyName}
                  className="bold padding-l-25"
                >
                    {policy.PolicyName}
                </Td>
                <Td column="Arn">
                  {arnEyeIcon}
                </Td>
                <Td column="AttachmentCount">
                  <strong>
                    {policy.AttachmentCount}
                    &nbsp;attachment{policy.AttachmentCount > 1 && 's'}</strong>
                </Td>
                <Td column="DefaultVersionId">
                  <strong>{policy.DefaultVersionId}</strong>
                </Td>
                <Td column="UpdateDate">
                    {getDateFormat(new Date(policy.UpdateDate))}
                </Td>
                <Td column="Actions">
                  {actGroup}
                </Td>
            </Tr>
        );
    }

    handleUserAction() {
        if (this.mounted) {
            this.setState({
                policies: [],
                filterText: '',
                currentIndex: 0,
                maxIndex: -1,
                marker: null,
            }, this.refresh);
        }
    }


    // To handle table filter change
    handleFilterChange(e) {
        if (this.mounted) {
            this.setState({ filterText: e.target.value });
        }
    }

    handlePreviousPage() {
        if (this.mounted) {
            this.setState({
                currentIndex: this.state.currentIndex - 1,
            });
        }
    }

    handleNextPage() {
        const currentIndex = this.state.currentIndex;
        if (this.mounted) {
            this.setState({
                currentIndex: this.state.currentIndex + 1,
            });
        }
        if (currentIndex + 1 === this.state.policies.length) {
            this.refresh();
        }
    }

    copyArnToClipboard(value) {
        const success = copyToClipboard(value);
        this.setCopied(success);
    }

    setCopied(success) {
        if (success && this.mounted) {
            this.setState({ copiedState: true });
            setTimeout(() => {
                this.setState({ copiedState: false });
            }, 2000);
        }
    }

    // Refresh users list
    refresh() {
        if (this.mounted) {
            this.setState({ dataLoaded: false });
        }
        PoliciesAPI.getPolicies(
          this.state.nbItems, this.state.marker
        ).then(res => {
            const policiesPages = this.state.policies;
            policiesPages.push(res.policies);
            if (this.mounted) {
                this.setState({
                    policies: policiesPages,
                    marker: res.marker,
                    maxIndex: res.marker ? -1 : this.state.currentIndex,
                    dataLoaded: true,
                });
            }
        }).catch(message => {
            if (message.status === 401 || message.status === 403) {
                if (message.response.body
                  && message.response.body.error === 'The AWS access key Id ' +
                    'you provided does not exist in our records.') {
                    window.location.href =
                        `${namespace}/login/error/invalidkey`;
                } else {
                    window.location.href =
                        `${namespace}/login/error/${message.status}`;
                }
            }
        }
        );
    }


    render() {
        let policiesList;
        const currentPolicies = this.state.policies[this.state.currentIndex];
        if (currentPolicies) {
            // Map over the contacts and get an element for each of them
            policiesList = currentPolicies.map(
              policy => this.getPolicyRow(policy)
            );
        }

        const arnPopover = (
            <Popover id="popover-policy-arn" title="Amazon Resource Name (ARN)">
                <p>
                    {'Each IAM policy has a unique ARN with the format: '}
                </p>
                <p>
                    {'arn:<partition>:<service>::<account-id>:'}
                    {'<resourcetype/resource>'}
                </p>
            </Popover>
        );

        const arnHelpIcon = (
            <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="right"
                overlay={arnPopover}>
                    <i className="fa fa-question-circle-o"></i>
            </OverlayTrigger>
        );


        const table = <Table
          className="table table-striped"
          id="table"
          sortable={[
              'PolicyName',
              'AttachmentCount',
              'DefaultVersionId',
              {
                  column: 'UpdateDate',
                  sortFunction: compareDates,
              },
          ]}
          filterBy={this.state.filterText}
          hideFilterInput
          filterable={['PolicyName', 'Arn']}>
            <Thead>
                <Th column="PolicyName" className="padding-l-25">
                    <strong className="name-header">Name</strong>
                </Th>
                <Th column="Arn">
                    <strong className="name-header">ARN</strong>
                    {' '}{arnHelpIcon}
                </Th>
                <Th column="AttachmentCount">
                    <strong className="name-header">Attachments</strong>
                </Th>
                <Th column="DefaultVersionId">
                    <strong className="name-header">Version</strong>
                </Th>
                <Th column="UpdateDate">
                    <strong className="name-header">Last Updated on</strong>
                </Th>
                <Th column="Actions">
                    <strong className="name-header">Actions</strong>
                </Th>
            </Thead>
            {policiesList}
        </Table>;

        const prevDisabled = this.state.currentIndex === 0;
        const nextDisabled = this.state.currentIndex === this.state.maxIndex;

        const pagerInstance = (
          <ul className="pager padding-l-15 padding-r-15">
            <li
              className={prevDisabled ? 'previous disabled' : 'previous'}
            >
              <a
                className="clickable"
                onClick={this.handlePreviousPage}
                style={brandingStyles.buttons.primary}
              >&larr;</a>
            </li>
            <li
              className={nextDisabled ? 'next disabled' : 'next'}
            >
              <a
                className="clickable"
                onClick={this.handleNextPage}
                style={brandingStyles.buttons.primary}
              >&rarr;</a>
            </li>
          </ul>
        );


        return (
            <div id="page-wrapper" style={brandingStyles.backgrounds.main}>
                <PageHeader facode="key" title="Policies"/>
                <div className="padding-b-15">
                    <CreatePolicyButton
                      callback={this.handleUserAction}
                    />
                </div>
                <div
                  className="panel panel-default"
                  style={brandingStyles.backgrounds.panel}
                >
                    <div className="panel-body padding-0">
                        <input
                          className="form-control full-width"
                          style={brandingStyles.backgrounds.input}
                          placeholder="Find policies"
                          value={this.state.filterText}
                          onChange={this.handleFilterChange}
                        >
                        </input>
                        <div className="table-responsive">
                          {this.state.dataLoaded
                            ? table : <LoadingSpinner />}
                        </div>
                        {pagerInstance}
                    </div>
                </div>
            </div>
        );
    }
}
export default PoliciesComponent;



/** WEBPACK FOOTER **
 ** ./src/pages/Policies.js
 **/